/**
 * agentRuntime — deriva o ESTADO RUNTIME dos agentes a partir dos eventos do
 * trace (timeline) + roster do squad. Alimenta o AgentRoom (visão animada) e o
 * runtime overlay da Topologia. Projeção read-only: não orquestra nada.
 *
 * Mapeia cada evento (agent_id + phase + message) para uma ATIVIDADE visual,
 * espelhando a máquina de estados do handoff (idle → active → reading/typing/
 * thinking → waiting/failed/completed). Função PURA (testável).
 */

/** Classifica a atividade visual de um evento pelo seu conteúdo. */
export function classifyActivity(message = '', phase = '', eventType = '') {
  const m = String(message || '').toLowerCase();
  const et = String(eventType || '').toLowerCase();

  if (et.includes('failed') || m.includes('❌') || m.includes('falhou') || m.includes('erro'))
    return 'failed';
  if (m.includes('⏸') || m.includes('aguardando') || m.includes('aguardar'))
    return 'waiting';
  if (m.includes('📝') || m.includes('write_file') || m.includes('gerando') ||
      m.includes('projetad') || m.includes('escrevendo') || m.includes('escrito'))
    return 'typing';
  if (m.includes('🔍') || m.includes('pendente') || m.includes('lendo') ||
      m.includes('pesquis') || m.includes('analisa') || m.includes('lê'))
    return 'reading';
  if (m.includes('📋') || m.includes('planej') || m.includes('inventário') ||
      m.includes('design') || m.includes('pensa'))
    return 'thinking';
  if (m.includes('✓') || m.includes('concluíd') || m.includes('complete') ||
      m.includes('aprovad'))
    return 'completed';
  return 'active';
}

/** Rótulo curto e humano da atividade (para a bolha do personagem). */
export const ACTIVITY_LABEL = {
  idle:      'ocioso',
  active:    'trabalhando',
  reading:   'analisando',
  typing:    'gerando',
  thinking:  'planejando',
  waiting:   'aguardando',
  failed:    'falhou',
  completed: 'concluído',
};

// Agentes que atuam como SUBAGENTES do tech-lead (delegação).
const _SUBAGENTS = new Set(['codegen-agent']);
const _ORCHESTRATOR = 'tech-lead-agent';

function _cleanMsg(msg) {
  // remove o prefixo [codegen-agent] e emojis de controle do início
  return String(msg || '')
    .replace(/^\[[^\]]+\]\s*/, '')
    .replace(/^[^\wÀ-ú]+/, '')
    .trim()
    .slice(0, 80);
}

/**
 * Deriva as views runtime dos agentes.
 * @param {Array<{seq,agent_id,phase,message,event_type}>} events - eventos ordenados
 * @param {Array<{agent_id,node_label,tier}>} squadNodes - roster do squad
 * @param {boolean} execTerminal - execução já terminou?
 * @returns {Array} agentes com {agentId,name,status,activity,label,lastSeq,atuou,eventos,isSubagent,parentAgentId,active}
 */
export function deriveAgentRuntime(events = [], squadNodes = [], execTerminal = false) {
  const byId = new Map();

  const ensure = (agentId, name) => {
    if (!agentId) return null;
    if (!byId.has(agentId)) {
      byId.set(agentId, {
        agentId,
        name: name || agentId,
        status: 'idle',
        activity: '',
        label: ACTIVITY_LABEL.idle,
        lastSeq: -1,
        // atuou nesta execução (assinou ao menos um evento) × só está no squad
        atuou: false,
        eventos: 0,
        isSubagent: _SUBAGENTS.has(agentId),
        parentAgentId: _SUBAGENTS.has(agentId) ? _ORCHESTRATOR : null,
        active: false,
      });
    } else if (name && byId.get(agentId).name === agentId) {
      byId.get(agentId).name = name;
    }
    return byId.get(agentId);
  };

  // Roster do squad (idle) + orquestrador sempre presente.
  ensure(_ORCHESTRATOR, 'Tech Lead');
  for (const n of squadNodes || []) {
    ensure(n.agent_id || n.node_label, n.node_label || n.agent_id);
  }

  // Aplica os eventos em ordem: o ÚLTIMO evento de cada agente define seu estado.
  let lastAgentId = null;
  for (const ev of events || []) {
    const aid = ev.agent_id;
    if (!aid) continue;
    const a = ensure(aid);
    a.atuou = true;
    a.eventos += 1;
    const seq = Number(ev.seq ?? 0);
    if (seq >= a.lastSeq) {
      a.lastSeq = seq;
      a.status = classifyActivity(ev.message, ev.phase, ev.event_type);
      a.label = ACTIVITY_LABEL[a.status] ?? a.status;
      a.activity = _cleanMsg(ev.message);
    }
    lastAgentId = aid;
  }

  // O agente do ÚLTIMO evento é o que está "ao vivo" — a menos que a execução
  // tenha terminado (aí ninguém está ativo). Os demais que estavam em atividade
  // (typing/reading/thinking/active) e não são o último → viram idle (saíram de cena).
  for (const a of byId.values()) {
    const isLive = a.agentId === lastAgentId && !execTerminal;
    a.active = isLive;
    if (!isLive && ['active', 'reading', 'typing', 'thinking'].includes(a.status)) {
      // manteve-se trabalhando mas não é mais o foco → repousa como idle
      a.status = 'idle';
      a.label = ACTIVITY_LABEL.idle;
      a.activity = '';
    }
  }

  // Ordena: orquestrador primeiro, depois o ativo, depois QUEM ATUOU na execução e, por
  // último, quem só está no squad (eb378ddc, 2026-10-06: 7 dos 16 nós nunca atuaram e
  // ficavam misturados aos demais como "ocioso").
  return Array.from(byId.values()).sort((x, y) => {
    if (x.agentId === _ORCHESTRATOR) return -1;
    if (y.agentId === _ORCHESTRATOR) return 1;
    if (x.active !== y.active) return x.active ? -1 : 1;
    if (x.atuou !== y.atuou) return x.atuou ? -1 : 1;
    return x.name.localeCompare(y.name);
  });
}
